import { useCallback, useContext, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import Moment from "moment";
import { Modal, Button } from "react-bootstrap";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import AuditLogService from "../../../services/AuditLogService";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const AuditLogsTable = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selectedTable, setSelectedTable] = useState("Users");
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Pagination states
  const [status, setStatus] = useState(0);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(20);
  const [pageNo, setPageNo] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [maxPageNo, setMaxPageNo] = useState(1);
  const pageOptions = [20, 50, 100];
  const navigate = useNavigate();

  const authContext = useContext(AuthContext);

  const tableOptions = [
    "QuizMCQOptions",
    "MCQQuestions",
    "CourseUsers",
    "MCQExamStatuses",
    "Incidents",
    "IncidentCategories",
    "CourseAttachments",
    "MCQOptions",
    "Documents",
    "VMList",
    "Metadatas",
    "Category",
    "IncidentStatuses",
    "DocumentAttachments",
    "VMSessions",
    "DocumentTypes",
    "QuizMCQExams",
    "MCQExams",
    "MenuPermissions",
    "Quiz",
    "Tickets",
    "QuizMCQExamAnswers",
    "Menus",
    "Roles",
    "QuizMCQExamStatuses",
    "Assessments",
    "QuizMCQQuestions",
    "Courses",
    "FAQs",
    "RolePermissions",
    "CourseIncidents",
    "UserOTPs",
    "UserRoles",
    "Users",
    "Agents",
    "AgentCourses",
  ];
  useEffect(() => {
    const auditlogAccess = authContext.getPermissions("auditlogs");
    if (auditlogAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
  }, [authContext, navigate]);

  const formatValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "0001-01-01T00:00:00"
    ) {
      return "-";
    }
    if (typeof value === "string" && value.includes("T")) {
      const date = Moment(value, Moment.ISO_8601, true);
      if (date.isValid()) return date.format("DD/MM/YYYY hh:mm A");
    }
    return String(value);
  };

const loadLogs = useCallback(async (signal) => { 
    setLoading(true);
    try {
      let data;
      if (status === 0) {
      // All 
        data = await AuditLogService.GetAuditLogs(
          selectedTable.toLowerCase(),
          skip,
          take,
          authContext,
          signal 
        );
      } else {
        data = await AuditLogService.GetAuditLogsByStatus(
          selectedTable.toLowerCase(),
          status,// "I" or "M" or "D"
          skip,
          take,
          authContext,
          signal
        );
      }

      if (signal?.aborted) return; 

      if (data && data.isSuccess) {
        setTotalRecords(data.data.totalRecords);
        const mPageNo = Math.ceil(data.data.totalRecords / take);
        setMaxPageNo(mPageNo === 0 ? 1 : mPageNo);
        setLogs(data.data.records || []);
      } else {
        AlertService.error(data?.errorMessage || "Failed to load audit logs");
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [selectedTable, status, skip, take, authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadLogs(controller.signal); 
    return () => controller.abort(); 
  }, [loadLogs]);

  const statusOptions = [
    { label: "All", value: 0, className: "all-btn" },
    { label: "Insert", value: "I", className: "closed-btn" },
    { label: "Modify", value: "M", className: "inprogress-btn" },
    { label: "Delete", value: "D", className: "cancelled-btn" },
  ];
  // When table changes, reset pagination
  const handleTableChange = (value) => {
    setSelectedTable(value);
    setStatus(0);
    setPageNo(1);
    setSkip(0);
  };

  const changeStatus = (value) => {
    setStatus(value);
    setPageNo(1);
    setSkip(0);
  };

  const goToPreviousPage = () => {
    let cPageNo = pageNo - 1;
    cPageNo = cPageNo > 0 ? cPageNo : 1;
    setPageNo(cPageNo);
    setSkip(cPageNo * take - take);
  };

  const goToNextPage = () => {
    let cPageNo = pageNo + 1;
    cPageNo = cPageNo <= maxPageNo ? cPageNo : maxPageNo;
    setPageNo(cPageNo);
    setSkip(cPageNo * take - take);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (maxPageNo <= 5) {
      for (let i = 1; i <= maxPageNo; i++) pages.push(i);
    } else {
      pages.push(1);
      if (pageNo > maxVisiblePages + 1) pages.push("...");

      let start = Math.max(2, pageNo - 1);
      let end = Math.min(maxPageNo - 1, pageNo + 1);

      if (pageNo <= maxVisiblePages) end = 4;
      else if (pageNo > maxPageNo - maxVisiblePages) start = maxPageNo - 3;

      for (let i = start; i <= end; i++) pages.push(i);

      if (pageNo < maxPageNo - maxVisiblePages) pages.push("...");
      if (maxPageNo > 1) pages.push(maxPageNo);
    }

    return pages;
  };

  const getChanges = (log) => {
    try {
      const oldObj = log.oldValue ? JSON.parse(log.oldValue) : {};
      const newObj = log.newValue ? JSON.parse(log.newValue) : {};
      const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
      return Array.from(keys)
        .filter((key) => oldObj[key] !== newObj[key])
        .map((key) => ({
          field: key,
          oldValue: oldObj[key],
          newValue: newObj[key],
        }));
    } catch {
      return [];
    }
  };

  const dateTemplate = (rowData) => {
    if (loading) return <Skeleton width="120px" height="25px" />;
    return Moment.utc(rowData.modifiedOn).local().format("DD/MM/YYYY hh:mm A");
  };

  const statusTemplate = (rowData) => {
    if (loading) return <Skeleton width="80px" height="25px" />;
    switch (rowData.status) {
      case "I":
        return <span className="text-success">Insert</span>;
      case "M":
        return <span className="text-primary">Modify</span>;
      case "D":
        return <span className="text-danger">Delete</span>;
      default:
        return rowData.status;
    }
  };

  const viewTemplate = (rowData) => {
    if (loading) return <Skeleton width="70px" height="25px" />;
    return (
      <button
        className="btn btn-sm btn-outline px-4 d-flex align-items-center gap-1 py-2"
        onClick={() => {
          setSelectedLog(rowData);
          setShowModal(true);
        }}
        title="View Changes"
      >
        <FaEye size={16} />
        <span>View</span>
      </button>
    );
  };

  const textSkeleton = (width = "100px") => (
    <Skeleton width={width} height="25px" />
  );

  const getPlaceholderRows = () =>
    Array.from({ length: take }, (_, i) => ({ id: `skeleton-${i}` }));

  return (
    <div className="container-fluid mt-3 pb-3">
      {/* Top Controls */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
        {/* Status Filter Buttons */}
        <div className="d-flex">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              className={`btn p-2 ${opt.className} ${status === opt.value ? "active-page" : ""}`}
              onClick={() => changeStatus(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Table Select */}
        <div className="d-flex align-items-center">
          <label className="me-2">Select Table:</label>
          <select
            className="form-select form-select-sm custom-input-color"
            style={{ width: "220px" }}
            value={selectedTable}
            onChange={(e) => handleTableChange(e.target.value)}
          >
            {tableOptions.map((table) => (
              <option key={table} value={table}>
                {table}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        value={loading ? getPlaceholderRows() : logs}
        scrollable
        scrollHeight="640px"
        className="custom-table"
      >
        <Column
          header="S.No"
          style={{ width: "30px" }}
          body={(_rowData, options) =>
            loading
              ? textSkeleton("20px")
              : (pageNo - 1) * take + options.rowIndex + 1
          }
        />
        <Column
          field="tableName"
          header="Table"
          style={{ width: "120px" }}
          body={(rowData) =>
            loading ? textSkeleton("80px") : rowData.tableName
          }
        />
        <Column
          field="changeNature"
          header="Action"
          style={{ width: "350px" }}
          body={(rowData) =>
            loading ? textSkeleton("250px") : rowData.changeNature
          }
        />
        <Column
          field="modifiedByDisplayName"
          header="Modified By"
          style={{ width: "180px" }}
          body={(rowData) =>
            loading ? textSkeleton("80px") : rowData.modifiedByDisplayName
          }
        />
        <Column
          header="Modified On"
          style={{ width: "140px" }}
          body={dateTemplate}
        />
        <Column
          header="Changes"
          style={{ width: "120px", textAlign: "center" }}
          body={viewTemplate}
        />
      </DataTable>

      {/* Pagination */}
      <div className="d-flex flex-column flex-start flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        {/* Left: Total Records + Show Rows */}
        <div className="d-flex align-items-center gap-3">
          <span>
            Total Records:{" "}
            <span className="text-secondary">{totalRecords}</span>
          </span>
          <div className="d-flex align-items-center">
            <label className="me-2">Show Rows:</label>
            <select
              className="form-select form-select-sm w-auto text-secondary custom-input-color"
              value={take}
              onChange={(e) => {
                setTake(parseInt(e.target.value));
                setPageNo(1);
                setSkip(0);
              }}
            >
              {pageOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Right: Page Buttons */}
        <div className="d-flex justify-content-center align-items-center gap-2">
          <button
            className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={goToPreviousPage}
            disabled={pageNo === 1}
          >
            &lt;
          </button>

          {generatePageNumbers().map((page, index) =>
            page === "..." ? (
              <span key={index} className="mx-1 text-secondary">
                ...
              </span>
            ) : (
              <button
                key={index}
                className={`btn p-0 d-flex align-items-center justify-content-center text-secondary pagination-item ${pageNo === page ? "active-page" : ""}`}
                style={{
                  width: "40px",
                  height: "40px",
                  transition: "all 0.2s",
                }}
                onClick={() => {
                  setPageNo(page);
                  setSkip(page * take - take);
                }}
              >
                {page}
              </button>
            ),
          )}

          <button
            className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={goToNextPage}
            disabled={pageNo >= maxPageNo}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Audit Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: "75vh",
            overflowY: "auto",
            overscrollBehavior: "none",
          }}
        >
          {selectedLog && (
            <div>
              <div className="mb-3">
                <strong>Table:</strong> {selectedLog.tableName} <br />
                <strong>Action:</strong>{" "}
                {selectedLog.status === "I"
                  ? "Insert"
                  : selectedLog.status === "M"
                    ? "Modify"
                    : selectedLog.status === "D"
                      ? "Delete"
                      : selectedLog.status}
                <br />
                <strong>Modified By:</strong>{" "}
                {formatValue(selectedLog.modifiedBy)} <br />
                <strong>Date:</strong>{" "}
                {Moment(selectedLog.modifiedOn).format("DD/MM/YYYY hh:mm A")}
              </div>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th className="table-color">Field</th>
                    <th className="table-color">Old Value</th>
                    <th className="table-color">New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {getChanges(selectedLog).length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center">
                        No field changes detected
                      </td>
                    </tr>
                  ) : (
                    getChanges(selectedLog).map((c, i) => (
                      <tr key={i}>
                        <td className="table-color">{c.field}</td>
                        <td
                          className="table-color"
                          style={{
                            maxWidth: "300px",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {formatValue(c.oldValue)}
                        </td>
                        <td
                          className="table-color"
                          style={{
                            maxWidth: "300px",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {formatValue(c.newValue)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AuditLogsTable;
